import type {
  PublicIssueStatement,
  ReportStatusItem,
} from '../../api/DigiobcanApi';
import { __, declension, formatDateTime } from '../../utils';
import { FormattedText } from '../FormattedText';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  alpha,
  Box,
  ButtonBase,
  EmptyState,
  Skeleton,
  Stack,
  StatusChip,
  type StatusChipTone,
} from '@presov.sk/presov-ui-react';
import React from 'react';

/**
 * Ako sa občanovi hlási kategória, v ktorej sa jeho podnet rieši.
 *
 * Duplikát má od digiobcan-api 6.22 **stav originálu** (`publicIssueStatusSql`
 * na API, moj-presov#36) a vyjadrenia originálu, takže `duplicate` sem z API
 * nechodí. Riadok `duplicate` nižšie je len záloha pre staršie API. Predtým
 * tu platilo „duplikát = V riešení" a pri neprijatom origináli to klamalo:
 * feed ten istý podnet ukazoval ako „Nové".
 *
 * Farby sú zo zadania a **nie sú tie isté, aké má štítok podnetu v karte
 * zoznamu** ([[feedStatus]]): tam je semafor nad celým podnetom (Nové /
 * V riešení / Vyriešené), tu je stav jednej kategórie a k nemu časová os, kde
 * žltá už patrí kroku „Prijaté do riešenia". Keby bola žltá aj na štítku
 * kategórie, dva rôzne stavy na tej istej karte by mali jednu farbu.
 */
const FINDING_STATUS: Record<
  string,
  { label: string; tone: StatusChipTone; dot: string }
> = {
  resolved: { label: __('Vyriešené'), tone: 'success', dot: 'green.main' },
  accepted: { label: __('V riešení'), tone: 'info', dot: 'blue.main' },
  duplicate: { label: __('V riešení'), tone: 'info', dot: 'blue.main' },
};

const FALLBACK_STATUS = {
  label: __('Prijaté'),
  tone: 'warning' as StatusChipTone,
  dot: 'yellow.main',
};

/**
 * Autor vyjadrenia. API meno úradníka **zámerne nezverejňuje** - občan dostáva
 * stanovisko úradu, nie konkrétneho človeka (`PublicIssueStatement` oproti
 * `IssueStatement`). Zadanie počíta s názvom odboru; ten v dátach nie je, tak
 * tu stojí mesto ako celok. Keď odbor v API pribudne, mení sa len tento riadok.
 */
const STATEMENT_AUTHOR = __('Mesto Prešov · zverejnené pri podnete');

type Finding = ReportStatusItem;

/** Jedna udalosť v časovej osi nálezu. */
type TimelineEvent = {
  key: string;
  label: string;
  at?: string;
  dot: string;
  muted?: boolean;
  statement?: PublicIssueStatement;
};

/**
 * Kedy sa nález uzavrel.
 *
 * API čas uzavretia **nevracia** (`ReportStatusItem` má len `date`, teda kedy
 * nález vznikol), takže sa berie z finálneho stanoviska - tým sa nález
 * v administrácii uzatvára, takže je to ten istý okamih až na sekundy. Bez
 * finálneho stanoviska zostane krok „Vyriešené" bez dátumu; vymyslieť ho
 * z čohokoľvek iného by bolo horšie než ho nenapísať.
 */
const resolvedAt = (finding: Finding): string | undefined =>
  finding.statements?.find((statement) => statement.final)?.inserted_at;

/**
 * Chronológia jedného nálezu zhora dolu, tak ako sa stala: prijatie,
 * vyjadrenia, uzavretie. Vyjadrenia idú **celé a od najstaršieho** - API ich
 * vracia od najnovšieho, ale priebeh je to, čo občan prišiel čítať, takže sa
 * číta v čase a nič sa neskrýva za „zobraziť viac".
 */
const timeline = (finding: Finding): TimelineEvent[] => {
  const events: TimelineEvent[] = [
    {
      key: 'accepted',
      label: __('Prijaté do riešenia'),
      at: finding.date,
      dot: 'yellow.main',
    },
  ];

  (finding.statements ?? [])
    .slice()
    .reverse()
    .forEach((statement) => {
      events.push({
        key: statement.statement_id,
        label: statement.final
          ? __('Finálne stanovisko')
          : __('Priebežné stanovisko'),
        at: statement.inserted_at,
        dot: statement.final ? 'green.main' : 'blue.main',
        statement,
      });
    });

  if (finding.status === 'resolved') {
    events.push({
      key: 'resolved',
      label: __('Vyriešené'),
      at: resolvedAt(finding),
      dot: 'green.main',
    });
  } else {
    events.push({
      key: 'pending',
      label: __('Zatiaľ neuzavreté'),
      dot: 'grey.300',
      muted: true,
    });
  }

  return events;
};

/** Hlavička karty: `Uzavreté 28.8.2026 11:15 · 3 vyjadrenia`. */
const summary = (finding: Finding): string => {
  const count = finding.statements?.length ?? 0;
  const counted = `${count} ${declension(count, [
    __('vyjadrenie'),
    __('vyjadrenia'),
    __('vyjadrení'),
  ])}`;

  const closed =
    finding.status === 'resolved' ? resolvedAt(finding) : undefined;
  const when = finding.status === 'resolved' ? closed : finding.date;
  const label = finding.status === 'resolved' ? __('Uzavreté') : __('Prijaté');

  return [when ? `${label} ${formatDateTime(new Date(when))}` : label, counted]
    .filter(Boolean)
    .join(' · ');
};

/** Bublina vyjadrenia - jediná zvýraznená vec v karte je to finálne. */
const StatementBubble: React.FC<{ statement: PublicIssueStatement }> = ({
  statement,
}) => (
  <Stack
    sx={(theme) => ({
      gap: 1,
      padding: theme.spacing(1.25, 1.5),
      borderRadius: `${theme.typography.pxToRem(12)}`,
      backgroundColor: statement.final
        ? theme.palette.blue.surface
        : theme.palette.grey[50],
      // Modrá nemá `surfaceBorder` (má ho zatiaľ len žltá), takže rám je tá
      // istá modrá stlmená na silu tintu - `divider` by na modrej ploche
      // vyzeral ako špina, rovnako ako to hovorí komentár pri `surfaceBorder`.
      border: `${theme.typography.pxToRem(1)} solid ${
        statement.final
          ? alpha(theme.palette.blue.main, 0.25)
          : theme.palette.divider
      }`,
    })}
  >
    <FormattedText text={statement.text} />

    {statement.media.length ? (
      <Stack sx={{ gap: 0.75, alignItems: 'flex-start' }}>
        {statement.media.map((media) => (
          <Box
            key={media.media_id}
            component='a'
            href={media.public_url}
            target='_blank'
            rel='noopener noreferrer'
            sx={(theme) => ({
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.75,
              maxWidth: 1,
              fontSize: theme.typography.pxToRem(12),
              color: theme.palette.primary.dark,
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' },
            })}
          >
            <DescriptionOutlinedIcon sx={{ fontSize: 14, flex: 'none' }} />
            <Box
              component='span'
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {media.original_filename ?? media.media_id}
            </Box>
          </Box>
        ))}
      </Stack>
    ) : null}

    <Box
      component='span'
      sx={(theme) => ({
        fontSize: theme.typography.pxToRem(11),
        color: theme.palette.grey[500],
      })}
    >
      {STATEMENT_AUTHOR}
      {/* Upravené vyjadrenie (moj-presov#18): občan vidí len najnovšie znenie
      a tento štítok, históriu úprav nie. Kedy, prezradí bublina pri myši. */}
      {statement.edited_at && (
        <span
          title={`${__('Upravené')} ${formatDateTime(new Date(statement.edited_at))}`}
        >
          {` · ${__('upravené')}`}
        </span>
      )}
    </Box>
  </Stack>
);

/**
 * Jedna kategória so svojou celou chronológiou.
 *
 * **Zabalená.** Zadanie ju kreslilo predvolene rozbalenú, ale pri troch
 * kategóriách a piatich vyjadreniach v každej z nich je otvorené všetko naraz
 * neprehľadné - občan hľadá, kde jedna kategória končí a druhá začína. Hlavička
 * pritom nesie to podstatné (kategória, stav, kedy a koľko vyjadrení), takže
 * rozbaliť treba len tú, ktorú si ide naozaj prečítať.
 */
const FindingCard: React.FC<{ finding: Finding }> = ({ finding }) => {
  const [open, setOpen] = React.useState(false);
  const status = FINDING_STATUS[finding.status] ?? FALLBACK_STATUS;
  const events = React.useMemo(() => timeline(finding), [finding]);

  return (
    <Stack
      sx={(theme) => ({
        flex: 'none',
        overflow: 'hidden',
        borderRadius: `${theme.typography.pxToRem(14)}`,
        border: `${theme.typography.pxToRem(1)} solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
      })}
    >
      <ButtonBase
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
        sx={(theme) => ({
          alignItems: 'flex-start',
          gap: 1.25,
          padding: theme.spacing(1.5, 1.625),
          textAlign: 'left',
          backgroundColor: open
            ? theme.palette.grey[50]
            : theme.palette.background.paper,
        })}
      >
        <Box
          sx={{
            flex: 'none',
            width: 8,
            height: 8,
            marginTop: '7px',
            borderRadius: '999px',
            backgroundColor: status.dot,
          }}
        />

        <Stack sx={{ flex: 1, minWidth: 0, gap: 0.5 }}>
          <Box
            component='span'
            sx={(theme) => ({
              fontSize: theme.typography.pxToRem(14),
              fontWeight: 700,
              lineHeight: 1.3,
              wordBreak: 'break-word',
            })}
          >
            {finding.categoryName}
          </Box>
          <Box
            component='span'
            sx={(theme) => ({
              fontSize: theme.typography.pxToRem(12),
              color: theme.palette.grey[500],
            })}
          >
            {summary(finding)}
          </Box>
        </Stack>

        <Stack
          direction='row'
          sx={{ flex: 'none', alignItems: 'center', gap: 1 }}
        >
          <StatusChip tone={status.tone}>{status.label}</StatusChip>
          <ExpandMoreIcon
            sx={(theme) => ({
              fontSize: 18,
              color: theme.palette.grey[500],
              transform: open ? 'rotate(180deg)' : 'none',
              transition: 'transform .15s',
            })}
          />
        </Stack>
      </ButtonBase>

      {open ? (
        <Stack
          sx={(theme) => ({
            gap: 1.75,
            margin: theme.spacing(0, 1.625, 1.75, 2.75),
            paddingLeft: 2,
            borderLeft: `${theme.typography.pxToRem(
              1.5,
            )} solid ${theme.palette.divider}`,
          })}
        >
          {events.map((event) => (
            <Stack key={event.key} sx={{ position: 'relative', gap: 0.625 }}>
              {/* Gulička sedí **na** linke časovej osi, nie vedľa nej. Posun
                  je odpočet, nie odhad: odsadenie zoznamu (16) plus polovica
                  hrúbky linky (0,75) plus polovica guličky (5,5). Biely
                  prstenec pod ňou linku prekryje, takže cez guličku
                  nepresvitá. */}
              <Box
                sx={(theme) => ({
                  position: 'absolute',
                  left: theme.typography.pxToRem(-(16 + 0.75 + 5.5)),
                  top: 2,
                  width: 11,
                  height: 11,
                  borderRadius: '999px',
                  backgroundColor: event.dot,
                  boxShadow: `0 0 0 3px ${theme.palette.background.paper}`,
                })}
              />

              <Stack
                direction='row'
                sx={{ alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}
              >
                <Box
                  component='span'
                  sx={(theme) => ({
                    fontSize: theme.typography.pxToRem(12.5),
                    fontWeight: 700,
                    color: event.muted
                      ? theme.palette.grey[500]
                      : theme.palette.text.primary,
                  })}
                >
                  {event.label}
                </Box>
                {event.at ? (
                  <Box
                    component='span'
                    sx={(theme) => ({
                      fontSize: theme.typography.pxToRem(11.5),
                      color: theme.palette.grey[500],
                    })}
                  >
                    {formatDateTime(new Date(event.at))}
                  </Box>
                ) : null}
              </Stack>

              {event.statement ? (
                <StatementBubble statement={event.statement} />
              ) : null}
            </Stack>
          ))}
        </Stack>
      ) : null}
    </Stack>
  );
};

type Props = {
  statuses: ReportStatusItem[];
  isLoading?: boolean;
};

/**
 * Vyjadrenia úradu v detaile podnetu - **jedna karta na kategóriu**.
 *
 * Podnet sa reálne rieši vo viacerých kategóriách naraz a každá má vlastný
 * stav, vlastný čas prijatia, vlastné vyjadrenia a vlastné uzavretie. Preto tu
 * nie je jeden trojkrokový priebeh, ktorý tu stál predtým: z neho občan nevedel,
 * kto a čo mu odpovedal, a pri troch kategóriách ukazoval stav jednej z nich.
 *
 * Pravidlá zo zadania, ktoré musia platiť:
 *
 * 1. **Presunuté nálezy sa občanovi nezobrazia.** Presun medzi kategóriami je
 *    interná operácia úradu. (API ich nevracia; filter je tu ako poistka.)
 * 2. **Bez potvrdenej kategórie sa nekreslí prázdna os**, ale stav „Čaká na
 *    prijatie na úrade".
 * 3. **Nikde sa nepoužije slovo „nález" ani „presunuté"** - pre občana je to
 *    kategória a vyjadrenie úradu.
 *
 * Karty sú predvolene **zabalené**, nie rozbalené ako v zadaní - dôvod je
 * v [[FindingCard]].
 */
const ReportStatements: React.FC<Props> = ({ statuses, isLoading = false }) => {
  const findings = React.useMemo(
    () =>
      statuses.filter(
        (item) => item.status !== 'moved' && item.status !== 'open',
      ),
    [statuses],
  );

  const countLabel = findings.length
    ? `${findings.length} ${declension(findings.length, [
        __('kategória'),
        __('kategórie'),
        __('kategórií'),
      ])}`
    : __('žiadna potvrdená kategória');

  return (
    <Stack sx={{ flex: 'none', gap: 1.25 }}>
      <Stack direction='row' sx={{ alignItems: 'baseline', gap: 1 }}>
        <Box
          component='span'
          sx={(theme) => ({
            fontSize: theme.typography.pxToRem(13),
            fontWeight: 700,
          })}
        >
          {__('Vyjadrenia úradu')}
        </Box>
        {isLoading ? null : (
          <Box
            component='span'
            sx={(theme) => ({
              fontSize: theme.typography.pxToRem(12),
              color: theme.palette.grey[500],
            })}
          >
            {countLabel}
          </Box>
        )}
      </Stack>

      {isLoading ? (
        <Skeleton variant='rounded' width='100%' height={96} />
      ) : findings.length ? (
        findings.map((finding) => (
          <FindingCard
            key={finding.issue_id ?? finding.categoryName}
            finding={finding}
          />
        ))
      ) : (
        <EmptyState
          compact
          icon='list'
          title={__('Čaká na prijatie na úrade')}
          description={__(
            'Podnet je zaevidovaný. Kým ho úrad nepriradí zodpovednému odboru, nemáme k nemu stanovisko.',
          )}
        />
      )}
    </Stack>
  );
};

export default ReportStatements;
